import { useEffect, useMemo, useRef, useState } from 'react'

import { AddButton } from '#app/routes/mocra+/_form+/components/add-button.tsx'
import { ContactOverlay } from '#app/routes/mocra+/_form+/components/contacts/overlay.tsx'
import {
	createEmptyContactDraft,
	type ContactCandidate,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import {
	combineCandidateLists,
	createCandidateSignature,
} from '#app/routes/mocra+/_form+/components/contacts/utils.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'

type Props = {
	readonly onSubmit: (draft: ContactDraft) => void
	readonly candidates?: readonly ContactCandidate[]
	readonly defaultValue?: ContactDraft
	readonly triggerLabel?: string
	readonly disabled?: boolean
	readonly className?: string
	readonly directorySource?: string
	readonly existingContacts?: readonly ContactDraft[]
}

export function ContactAdd({
	onSubmit,
	candidates,
	defaultValue,
	triggerLabel,
	disabled,
	className,
	directorySource,
	existingContacts,
}: Props) {
	const [open, setOpen] = useState(false)
	const {
		candidates: directoryCandidates,
		mergeContacts,
		upsertContact,
	} = useContactDirectory()
	const mergeSignatureRef = useRef<string | null>(null)

	useEffect(() => {
		if (!candidates?.length) {
			mergeSignatureRef.current = null
			return
		}
		const signature = candidates
			.map((candidate) => createCandidateSignature(candidate))
			.join('||')
		if (mergeSignatureRef.current === signature) return
		mergeSignatureRef.current = signature
		mergeContacts(candidates, { source: directorySource })
	}, [candidates, mergeContacts, directorySource])

	const combinedCandidates = useMemo(
		() => combineCandidateLists(directoryCandidates, candidates),
		[directoryCandidates, candidates],
	)

	const excludedEmails = useMemo(() => {
		if (!existingContacts?.length) return []
		return existingContacts
			.map((contact) => contact.email?.trim())
			.filter((email): email is string => Boolean(email && email.length))
	}, [existingContacts])

	const handleSubmit = (draft: ContactDraft) => {
		const entry = upsertContact(draft, { source: directorySource })
		const resolvedDraft: ContactDraft = {
			id: entry?.id ?? draft.id,
			title:
				draft.title?.trim().length > 0
					? draft.title.trim()
					: (entry?.title ?? draft.title ?? ''),
			name: entry?.name ?? draft.name.trim(),
			email: entry?.email ?? draft.email.trim(),
			phone: entry?.phone ?? draft.phone?.trim() ?? '',
		}
		onSubmit(resolvedDraft)
		setOpen(false)
	}

	return (
		<>
			<AddButton
				onClick={() => setOpen(true)}
				disabled={disabled}
				className={className}
			>
				{triggerLabel ?? 'Add contact'}
			</AddButton>
			<ContactOverlay
				mode="add"
				open={open}
				onOpenChange={setOpen}
				onSubmit={handleSubmit}
				initialValue={defaultValue ?? createEmptyContactDraft()}
				candidates={combinedCandidates}
				excludedEmails={excludedEmails}
				onClosed={() => setOpen(false)}
			/>
		</>
	)
}
